/**
 * Terminology library (SDTM/CDISC controlled terminology) API service.
 *
 * The generated SDK exposes these endpoints but omits the search/limit/offset query params (they
 * aren't declared in the OpenAPI spec), so this service issues authenticated fetches directly -
 * mirroring the pattern used for other spec-incomplete endpoints. Components consume this only via
 * the query hooks in `hooks/queries/useTerminology.ts`.
 */

import { authorizedGet } from "./sdk"

/**
 * A codelist entry as returned by the library search (summary form, without full value list).
 */
export interface LibraryCodelist {
  code: string
  name: string
  submission_value: string
  extensible: boolean
  value_count: number
  values_preview: string[]
}

/**
 * Paginated response from a terminology-library search.
 */
interface LibraryResponse {
  items: LibraryCodelist[]
  total: number
  version: string
}

/**
 * A single codelist with its full set of coded values.
 */
interface FullCodelist {
  code: string
  name: string
  submission_value: string
  extensible: boolean
  values: Array<{ code: string; decode: string; nci_code?: string }>
}

export const terminologyApiService = {
  /**
   * Searches the terminology library, paginated by offset.
   */
  search: (query: string, limit = 50, offset = 0): Promise<LibraryResponse> => {
    const params = `?search=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`
    return authorizedGet<LibraryResponse>(`/api/v1/terminology-library${params}`)
  },

  /**
   * Fetches a single codelist (including its full value list) by code.
   */
  getByCode: (code: string): Promise<FullCodelist> =>
    authorizedGet<FullCodelist>(`/api/v1/terminology-library/${code}`),
}
